import { useState } from 'react'
import type { AiqIndexReport, AiqTriangleReport } from '../../types/aiq-report'
import { AiqTriangleSection } from './AiqTriangleSection'
import { AiqIndexSection } from './AiqIndexSection'

type TabId = 'pre' | 'post' | 'comparison'

interface Tab {
  id: TabId
  label: string
}

const TABS: Tab[] = [
  { id: 'pre', label: 'Pre-AIQ' },
  { id: 'post', label: 'Post-AIQ' },
  { id: 'comparison', label: '비교' },
]

interface ReportTabLayoutProps {
  standardReport: AiqTriangleReport
  advancedReport: AiqTriangleReport | null
  aiqIndexReport: AiqIndexReport | null
  hasPostAiq: boolean
}

export function ReportTabLayout({ standardReport, advancedReport, aiqIndexReport, hasPostAiq }: ReportTabLayoutProps) {
  const [activeTab, setActiveTab] = useState<TabId>(hasPostAiq ? 'comparison' : 'pre')

  const isTabDisabled = (tabId: TabId) => !hasPostAiq && (tabId === 'post' || tabId === 'comparison')

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Tab bar */}
      <div className="flex border-b border-gray-200">
        {TABS.map((tab) => {
          const disabled = isTabDisabled(tab.id)
          return (
            <button
              key={tab.id}
              onClick={() => !disabled && setActiveTab(tab.id)}
              disabled={disabled}
              className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-b-2 border-indigo-600 text-indigo-600 bg-indigo-50/50'
                  : disabled
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              {tab.label}
            </button>
          )
        })}
      </div>

      {/* Tab content */}
      <div className="p-6">
        {isTabDisabled(activeTab) ? (
          <div className="flex items-center justify-center py-12 text-gray-400">
            <p className="text-sm">Post-AIQ를 완료한 후 확인할 수 있습니다.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Standard Report */}
            <div>
              <h3 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-blue-500" />
                Standard Report
                <span className="text-xs font-normal text-gray-400">(정답률 기반)</span>
              </h3>
              <AiqTriangleSection
                report={standardReport}
                type="standard"
                phase={activeTab}
              />
            </div>

            {/* Advanced Report */}
            {advancedReport && (
              <div>
                <h3 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-purple-500" />
                  Advanced Report
                  <span className="text-xs font-normal text-gray-400">(시간 가중 점수)</span>
                </h3>
                <p className="text-xs text-gray-400 mb-3">
                  같은 문제를 맞춰도 빨리 풀수록 높은 점수를 받습니다.
                </p>
                <AiqTriangleSection
                  report={advancedReport}
                  type="advanced"
                  phase={activeTab}
                />
              </div>
            )}

            {/* AIQ 지수 (정규분포 변환) */}
            {aiqIndexReport && (
              <div>
                <h3 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-indigo-500" />
                  나의 AIQ 지수
                  <span className="text-xs font-normal text-gray-400">(정규분포 변환)</span>
                </h3>
                <AiqIndexSection report={aiqIndexReport} phase={activeTab} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
